import * as React from 'react';
import { CodeSnippet } from '../../../administration/models';
import { endpoints } from '../../../../config';
import { CSharpCodeEditorRuntime } from './internal/CSharpCodeEditorRuntime';
import { CSharpCodeEditorReadyHandle } from './types';

export type CSharpCodeEditorProps = {
    value: string;
    onChange: (value: string) => void;
    readOnly?: boolean;
    width?: string | number;
    height?: string | number;
    dataId?: string;
    snippets?: CodeSnippet[];
    onReady?: (editor: CSharpCodeEditorReadyHandle) => void;
};

const defaultProps = {
    width: '100%',
    height: '100%',
    value: '',
    readOnly: false,
    // eslint-disable-next-line no-empty,no-empty-function,@typescript-eslint/no-empty-function
    onChange: () => {},
    dataId: 'csharp-code-editor',
    snippets: new Array<CodeSnippet>()
};

// Reuse one runtime across modal remounts so the editor and LSP session stay warm,
// matching the old shared Monaco behavior and avoiding repeated cold starts.
let sharedRuntime: CSharpCodeEditorRuntime | undefined;

function processSize(size: string | number) {
    const normalizedSize = `${size}`;
    return /^\d+$/.test(normalizedSize) ? `${normalizedSize}px` : normalizedSize;
}

export default class CSharpCodeEditor extends React.Component<CSharpCodeEditorProps> {
    static defaultProps = defaultProps;

    private containerElement?: HTMLDivElement;

    private runtime: CSharpCodeEditorRuntime;

    private isMountedToDom = false;

    constructor(props: CSharpCodeEditorProps) {
        super(props);
        this.runtime = getOrCreateSharedRuntime(
            () =>
                new CSharpCodeEditorRuntime({
                    createUrl: createLspWebSocketUrl
                })
        );
    }

    componentDidMount() {
        this.isMountedToDom = true;
        this.initEditor();
    }

    componentDidUpdate(prevProps: CSharpCodeEditorProps) {
        if (prevProps.onChange !== this.props.onChange) {
            this.runtime.updateOnChange(this.props.onChange);
        }

        if (prevProps.value !== this.props.value) {
            this.runtime.updateValue(this.props.value ?? '');
        }

        if (prevProps.readOnly !== this.props.readOnly) {
            this.runtime.updateReadOnly(Boolean(this.props.readOnly));
        }

        if (prevProps.snippets !== this.props.snippets) {
            this.runtime.updateSnippets(this.props.snippets || []);
        }

        if (prevProps.width !== this.props.width || prevProps.height !== this.props.height) {
            this.runtime.layout();
        }
    }

    componentWillUnmount() {
        this.isMountedToDom = false;
        this.runtime.release(this.containerElement);
    }

    assignRef = (container: HTMLDivElement | null) => {
        if (!container) {
            return;
        }

        this.containerElement = container;
        this.runtime.attachToContainer(container);
    };

    render() {
        const fixedWidth = processSize(this.props.width || '100%');
        const fixedHeight = processSize(this.props.height || '100%');

        return (
            <div
                data-id={this.props.dataId ? `${this.props.dataId}-code-editor` : 'csharp-code-editor-container'}
                ref={this.assignRef}
                style={{
                    width: fixedWidth,
                    height: fixedHeight
                }}
                className="react-csharp-code-editor-container"
            />
        );
    }

    private initEditor() {
        if (!this.containerElement) {
            return;
        }

        this.runtime.attachToContainer(this.containerElement);
        this.runtime
            .initialize({
                value: this.props.value ?? '',
                onChange: this.props.onChange,
                readOnly: Boolean(this.props.readOnly),
                snippets: this.props.snippets || []
            })
            .then(editor => {
                if (this.isMountedToDom && this.props.onReady) {
                    this.props.onReady(editor);
                }
            })
            .catch(error => {
                console.error('Failed to initialize C# editor', error);
            });
    }
}

function createLspWebSocketUrl(): string {
    const nextUrl = new URL(endpoints.lspUrl as string, window.location.origin);
    nextUrl.protocol = nextUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    return nextUrl.toString();
}

function getOrCreateSharedRuntime(createRuntime: () => CSharpCodeEditorRuntime): CSharpCodeEditorRuntime {
    if (!sharedRuntime) {
        sharedRuntime = createRuntime();
    }

    return sharedRuntime;
}

export function disposeSharedCSharpCodeEditorRuntime(): void {
    sharedRuntime?.dispose();
    sharedRuntime = undefined;
}
